import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, ArrowUpRight, Check } from "lucide-react";
import { Reveal, MaskLineInView } from "@/components/motion/Reveal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { BRAND } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD =
  "w-full bg-transparent border-b py-3 text-[#1A1A1A] placeholder:text-[#4A4A4A]/50 focus:outline-none transition-colors duration-300";

const BUDGETS = ["₹5L - ₹10L", "₹10L - ₹20L", "₹20L+"];

const Consultation = () => {
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    location: "",
    budget_range: "",
    timeline: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [mapActive, setMapActive] = useState(false);

  const update = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Please enter your full name.";
    const digits = form.phone.replace(/\D/g, "");
    if (!form.phone.trim()) e.phone = "Please enter your phone number.";
    else if (digits.length < 10) e.phone = "Enter a valid phone number.";
    if (!form.location.trim()) e.location = "Please enter your project location.";
    if (!form.budget_range) e.budget_range = "Please select a budget range.";
    if (!form.timeline.trim()) e.timeline = "Please enter your timeline.";
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const eObj = validate();
    if (Object.keys(eObj).length) {
      setErrors(eObj);
      toast.error("Please complete all required fields correctly.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      setShowModal(true);
      toast.success("Enquiry received. Our studio will be in touch shortly.");
      setForm({ full_name: "", phone: "", location: "", budget_range: "", timeline: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const fieldCls = (k) =>
    `${FIELD} ${errors[k] ? "border-red-400 focus:border-red-500" : "border-[#D8D3CB] focus:border-[#C5A880]"}`;

  return (
    <div data-testid="consultation-page" className="bg-[#FAF9F6] pt-20">
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-20 md:pt-32 pb-24 md:pb-40">
        <div className="grid lg:grid-cols-12 gap-16 lg:gap-10">
          {/* Left — Form */}
          <div className="lg:col-span-7">
            <Reveal>
              <div className="overline text-[#8B7355] mb-8">Request a Consultation</div>
            </Reveal>
            <h1 className="font-serif-display text-[#1A1A1A] leading-[0.98] text-[clamp(2.25rem,6vw,5rem)] tracking-[-0.02em]">
              <MaskLineInView>Begin your</MaskLineInView>
              <MaskLineInView delay={0.1} className="italic text-[#C5A880]">bespoke journey.</MaskLineInView>
            </h1>
            <Reveal delay={0.15}>
              <p className="mt-6 text-[#4A4A4A] text-base md:text-lg max-w-lg leading-relaxed">
                Share a few details about your space. Our design principals personally review every enquiry from Noida &amp; Delhi NCR.
              </p>
            </Reveal>

            {done ? (
              <motion.div
                data-testid="consultation-success"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-14 border border-[#C5A880]/40 bg-[#F2EFEB] p-10"
              >
                <div className="w-12 h-12 rounded-full bg-[#C5A880] flex items-center justify-center mb-6">
                  <Check size={22} className="text-[#1A1A1A]" />
                </div>
                <h3 className="font-serif-display text-3xl text-[#1A1A1A]">Thank you.</h3>
                <p className="mt-3 text-[#4A4A4A] max-w-md leading-relaxed">
                  Your enquiry has reached our studio. Expect a personal response within one business day.
                </p>
                <button
                  data-testid="submit-another"
                  onClick={() => setDone(false)}
                  className="mt-8 text-[#8B7355] border-b border-[#8B7355]/40 pb-1 hover:text-[#1A1A1A] transition-colors duration-300"
                >
                  Submit another enquiry
                </button>
              </motion.div>
            ) : (
              <Reveal delay={0.2}>
                <form data-testid="consultation-form" onSubmit={handleSubmit} noValidate className="mt-14 grid sm:grid-cols-2 gap-x-8 gap-y-8">
                  <div className="sm:col-span-1">
                    <label className="overline text-[#8B7355] block mb-3">Full Name *</label>
                    <input data-testid="input-full-name" value={form.full_name} onChange={update("full_name")} className={fieldCls("full_name")} placeholder="Your name" />
                    {errors.full_name && <p data-testid="error-full-name" className="text-xs text-red-500 mt-2">{errors.full_name}</p>}
                  </div>
                  <div className="sm:col-span-1">
                    <label className="overline text-[#8B7355] block mb-3">Phone Number *</label>
                    <input data-testid="input-phone" inputMode="tel" value={form.phone} onChange={update("phone")} className={fieldCls("phone")} placeholder="+91" />
                    {errors.phone && <p data-testid="error-phone" className="text-xs text-red-500 mt-2">{errors.phone}</p>}
                  </div>
                  <div className="sm:col-span-2">
                    <label className="overline text-[#8B7355] block mb-3">Noida Society / Project Location *</label>
                    <input data-testid="input-location" value={form.location} onChange={update("location")} className={fieldCls("location")} placeholder="e.g. ATS Pristine, Sector 150" />
                    {errors.location && <p data-testid="error-location" className="text-xs text-red-500 mt-2">{errors.location}</p>}
                  </div>
                  <div className="sm:col-span-1">
                    <label className="overline text-[#8B7355] block mb-3">Estimated Budget *</label>
                    <Select value={form.budget_range} onValueChange={(v) => { setForm((f) => ({ ...f, budget_range: v })); setErrors((p) => ({ ...p, budget_range: undefined })); }}>
                      <SelectTrigger data-testid="select-budget" className={`w-full bg-transparent border-0 border-b rounded-none px-0 h-auto py-3 text-[#1A1A1A] focus:ring-0 data-[placeholder]:text-[#4A4A4A]/50 ${errors.budget_range ? "border-red-400" : "border-[#D8D3CB] focus:border-[#C5A880]"}`}>
                        <SelectValue placeholder="Select range" />
                      </SelectTrigger>
                      <SelectContent>
                        {BUDGETS.map((b) => (
                          <SelectItem key={b} value={b} data-testid={`budget-option-${b}`}>{b}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.budget_range && <p data-testid="error-budget" className="text-xs text-red-500 mt-2">{errors.budget_range}</p>}
                  </div>
                  <div className="sm:col-span-1">
                    <label className="overline text-[#8B7355] block mb-3">Project Timeline *</label>
                    <input data-testid="input-timeline" value={form.timeline} onChange={update("timeline")} className={fieldCls("timeline")} placeholder="e.g. Within 3 months" />
                    {errors.timeline && <p data-testid="error-timeline" className="text-xs text-red-500 mt-2">{errors.timeline}</p>}
                  </div>
                  <div className="sm:col-span-2">
                    <label className="overline text-[#8B7355] block mb-3">Anything else? (optional)</label>
                    <textarea data-testid="input-message" value={form.message} onChange={update("message")} rows={3} className={`${FIELD} border-[#D8D3CB] focus:border-[#C5A880] resize-none`} placeholder="Tell us about your vision" />
                  </div>

                  <div className="sm:col-span-2 mt-2">
                    <button
                      type="submit"
                      data-testid="consultation-submit"
                      disabled={submitting}
                      className="group inline-flex items-center gap-4 bg-[#1A1A1A] text-[#FAF9F6] px-10 py-4 hover:bg-[#C5A880] hover:text-[#1A1A1A] transition-colors duration-400 disabled:opacity-60"
                    >
                      <span className="text-sm tracking-wide">{submitting ? "Sending..." : "Submit Enquiry"}</span>
                      <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </button>
                  </div>
                </form>
              </Reveal>
            )}
          </div>

          {/* Right — Studio card */}
          <div className="lg:col-span-4 lg:col-start-9">
            <Reveal delay={0.1}>
              <div className="bg-[#1A1A1A] text-[#FAF9F6] p-10">
                <div className="overline text-[#C5A880] mb-8">The Studio</div>
                <ul className="space-y-7">
                  <li className="flex gap-4">
                    <Phone size={18} className="text-[#C5A880] mt-1 shrink-0" />
                    <div>
                      {BRAND.phones.map((p) => (
                        <a key={p} href={`tel:${p.replace(/\s/g, "")}`} className="block text-[#FAF9F6]/85 hover:text-[#C5A880] transition-colors duration-300">{p}</a>
                      ))}
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <Mail size={18} className="text-[#C5A880] mt-1 shrink-0" />
                    <a href={`mailto:${BRAND.email}`} className="text-[#FAF9F6]/85 hover:text-[#C5A880] transition-colors duration-300 break-all">{BRAND.email}</a>
                  </li>
                  <li className="flex gap-4">
                    <MapPin size={18} className="text-[#C5A880] mt-1 shrink-0" />
                    <div className="space-y-3 text-[#FAF9F6]/85 text-sm leading-relaxed">
                      {BRAND.locations.map((l) => <p key={l}>{l}</p>)}
                    </div>
                  </li>
                </ul>
              </div>

              <div className="mt-6 relative overflow-hidden border border-[#E5E5E5] h-[300px]">
                <iframe
                  title="Haven & Hue Studio Location"
                  data-testid="studio-map"
                  src="https://www.google.com/maps?q=Tata%20Eureka%20Park%20Sector%20150%20Noida&output=embed"
                  className={`w-full h-full grayscale-[30%] block border-0 transition-[filter] duration-300 ${mapActive ? "" : "pointer-events-none"}`}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                {!mapActive && (
                  <button
                    type="button"
                    data-testid="map-activate"
                    onClick={() => setMapActive(true)}
                    className="absolute inset-0 z-10 flex items-center justify-center bg-[#1A1A1A]/10 hover:bg-[#1A1A1A]/0 transition-colors duration-300 group"
                    aria-label="Activate map"
                  >
                    <span className="overline text-[#1A1A1A] bg-[#FAF9F6]/95 px-4 py-2.5 group-hover:bg-[#C5A880] transition-colors duration-300">
                      Click to interact
                    </span>
                  </button>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent data-testid="success-modal" className="bg-[#FAF9F6] border-[#C5A880]/40 sm:max-w-md">
          <DialogHeader>
            <div className="w-14 h-14 rounded-full bg-[#C5A880] flex items-center justify-center mb-4">
              <Check size={26} className="text-[#1A1A1A]" />
            </div>
            <DialogTitle className="font-serif-display text-3xl text-[#1A1A1A]">Enquiry Received</DialogTitle>
            <DialogDescription className="text-[#4A4A4A] leading-relaxed pt-2">
              Thank you for reaching out to Haven &amp; Hue. Our design principals personally review every enquiry and will respond within one business day.
            </DialogDescription>
          </DialogHeader>
          <button
            data-testid="modal-close"
            onClick={() => setShowModal(false)}
            className="mt-4 w-full bg-[#1A1A1A] text-[#FAF9F6] py-3.5 text-sm tracking-wide hover:bg-[#C5A880] hover:text-[#1A1A1A] transition-colors duration-400"
          >
            Continue Exploring
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Consultation;
